import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faTrash } from '@fortawesome/free-solid-svg-icons';
import BZAPIService from '../../services/BZAPIService';
import moment from 'moment';

const CommentModal = ({
  isOpen,
  userVersionData,
  toggle,
  isCve,
  projectVersionId,
  productName,
  productId,
  vendorId,
  fetchUserVersionState,
  selectedVersion,
  entityId,
  entityTitle
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const handleAddComment = async () => {
    if (newComment.trim()) {
      setIsSaving(true);
      const comment = {
        projectVersionId,
        productName,
        productId,
        vendorId,
        isCve,
        text: newComment
      };
      if (isCve) comment.cveId = entityId;
      else comment.bugId = entityId;
      const response = await BZAPIService.bsaCreateBugOrCveComment(comment);

      // Sort comments by timestamp in descending order (newest first)
      const sortedComments = [...response.comments].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setComments(sortedComments);
      fetchUserVersionState(selectedVersion);
      setNewComment('');
      setIsSaving(false);
    }
  };
  const handleDeleteComment = async commentId => {
    setIsDeleting(commentId);
    console.log('commentId x ver', commentId, userVersionData, {
      commentId,
      isCve,
      cveId: entityId,
      projectVersionId: userVersionData.id
    });

    if (isCve)
      await BZAPIService.bsaDeleteBugOrCveComment({
        commentId,
        isCve,
        cveId: entityId,
        projectVersionId: userVersionData.id
      });
    else
      await BZAPIService.bsaDeleteBugOrCveComment({
        commentId,
        isCve,
        bugId: entityId,
        projectVersionId: userVersionData.id
      });
    await fetchUserVersionState(selectedVersion);
    setIsDeleting(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  useEffect(() => {
    let fetchedComments = [];
    if (isCve) fetchedComments = userVersionData?.cves?.find(cve => cve.cveId === entityId)?.comments || [];
    else fetchedComments = userVersionData?.bugs?.find(bug => bug.bugId === entityId)?.comments || [];

    // Sort comments by timestamp in descending order (newest first)
    const sortedComments = [...fetchedComments].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setComments(sortedComments);
  }, [userVersionData, entityId, isCve]);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        Comments for {entityId}
        {entityTitle && entityTitle !== entityId && `: ${entityTitle}`}
      </ModalHeader>
      <ModalBody>
        <ul className="list-group">
          {comments.map((comment, index) => (
            <li key={index} className="list-group-item">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  <strong>{comment.userId}</strong>
                  <span className="ms-2 text-muted">{moment.utc(comment.timestamp).fromNow()}</span>
                </div>
                <Button color="danger" size="sm" onClick={() => handleDeleteComment(comment.id)}>
                  {isDeleting === comment.id ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
                </Button>
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{comment.text}</div>
            </li>
          ))}
        </ul>
        <textarea
          className="form-control mt-4"
          rows="3"
          placeholder="Add a comment"
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </ModalBody>
      <ModalFooter>
        <Button className="btn btn-falcon-default btn-sm" onClick={handleAddComment} disabled={!newComment || isSaving}>
          {isSaving ? <Spinner size="sm" /> : 'Add comment'}
        </Button>{' '}
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CommentModal.propTypes = {
  isOpen: PropTypes.bool,
  userVersionData: PropTypes.object,
  selectedVersion: PropTypes.object,
  fetchUserVersionState: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  isCve: PropTypes.bool,
  projectVersionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  productName: PropTypes.string,
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityTitle: PropTypes.string
};

export default CommentModal;
